import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  AlertCircle,
  Award,
  BookOpen,
  CalendarDays,
  CheckCircle2,
  ClipboardCheck,
  Clock,
  GraduationCap,
  Pencil,
  Plus,
  Search,
  Trash2,
  Users,
} from 'lucide-react'
import { apiJson, type Page } from '../api'
import { hasAnyRole } from '../auth'
import Pager from '../components/Pager'
import LearningCalendar from './LearningCalendar'
import Compliance, { complianceMeta, type Assignment } from './Compliance'

export interface Course {
  id: string
  title: string
  description?: string | null
  category: 'technical' | 'safety' | 'compliance' | 'leadership' | 'soft_skills'
  format: 'online' | 'classroom' | 'webinar' | 'blended'
  durationHours: number
  capacity: number
  instructorName?: string | null
  status: 'draft' | 'pending' | 'published' | 'archived'
  version: string
  certValidityMonths?: number | null
  enrolledCount: number
}

export interface Enrollment {
  id: string
  courseId: string
  courseName: string
  username: string
  status: 'enrolled' | 'in-progress' | 'completed' | 'dropped'
  progress: number
  enrolledAt: string
  completedAt?: string | null
  course: Pick<Course, 'id' | 'title' | 'category' | 'format' | 'durationHours' | 'status'> | null
}

export interface Certification {
  id: string
  courseId: string
  courseName: string
  username: string
  employeeName?: string | null
  score?: number | null
  issuedAt: string
  expiresAt?: string | null
  status: 'valid' | 'expiring-soon' | 'expired'
}

export const categoryMeta: Record<string, { label: string; cls: string }> = {
  technical: { label: 'Technical', cls: 'bg-sky-50 text-sky-700 ring-sky-100' },
  safety: { label: 'Safety', cls: 'bg-amber-50 text-amber-700 ring-amber-100' },
  compliance: { label: 'Compliance', cls: 'bg-violet-50 text-violet-700 ring-violet-100' },
  leadership: { label: 'Leadership', cls: 'bg-brand-50 text-brand-700 ring-brand-100' },
  soft_skills: { label: 'Soft Skills', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100' },
}

const statusMeta: Record<string, { label: string; cls: string }> = {
  draft: { label: 'Draft', cls: 'bg-slate-100 text-slate-600 ring-slate-200' },
  pending: { label: 'Pending', cls: 'bg-amber-50 text-amber-700 ring-amber-100' },
  published: { label: 'Published', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100' },
  archived: { label: 'Archived', cls: 'bg-slate-100 text-slate-400 ring-slate-200' },
}

const enrollStatusMeta: Record<string, { label: string; cls: string }> = {
  enrolled: { label: 'Enrolled', cls: 'bg-sky-50 text-sky-700 ring-sky-100' },
  'in-progress': { label: 'In progress', cls: 'bg-amber-50 text-amber-700 ring-amber-100' },
  completed: { label: 'Completed', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100' },
  dropped: { label: 'Dropped', cls: 'bg-slate-100 text-slate-400 ring-slate-200' },
}

const certStatusMeta: Record<string, { label: string; cls: string }> = {
  valid: { label: 'Valid', cls: 'bg-emerald-50 text-emerald-700 ring-emerald-100' },
  'expiring-soon': { label: 'Expiring soon', cls: 'bg-amber-50 text-amber-700 ring-amber-100' },
  expired: { label: 'Expired', cls: 'bg-red-50 text-red-700 ring-red-100' },
}

function fmtDate(iso?: string | null) {
  return iso ? iso.slice(0, 10) : '—'
}

function formatLabel(format: string) {
  return format.charAt(0).toUpperCase() + format.slice(1)
}

export default function Learning() {
  const navigate = useNavigate()
  const allowManage = hasAnyRole('admin', 'hr')
  const isAdmin = hasAnyRole('admin')
  const [tab, setTab] = useState<'mine' | 'catalog' | 'certs' | 'calendar' | 'manage' | 'compliance'>('mine')

  // ---- shared: my enrollments (drives My learning + catalog enrolled state) ----
  const [myEnrollments, setMyEnrollments] = useState<Enrollment[] | null>(null)
  const [mineError, setMineError] = useState('')

  // ---- required training: my assignments keyed by courseId ----
  const [myAssignments, setMyAssignments] = useState<Assignment[]>([])

  useEffect(() => {
    apiJson<Assignment[]>('/assignments/me')
      .then(setMyAssignments)
      .catch(() => setMyAssignments([]))
  }, [])

  const assignmentByCourse = useMemo(
    () => new Map(myAssignments.map((a) => [a.courseId, a] as const)),
    [myAssignments],
  )

  const fetchMine = useCallback(async () => {
    try {
      setMyEnrollments(await apiJson<Enrollment[]>('/enrollments/me'))
      setMineError('')
    } catch (err) {
      setMineError(err instanceof Error ? err.message : 'Failed to load your enrollments')
    }
  }, [])

  useEffect(() => {
    fetchMine()
  }, [fetchMine])

  const enrolledCourseIds = useMemo(
    () =>
      new Map(
        (myEnrollments ?? [])
          .filter((e) => e.status !== 'dropped')
          .map((e) => [e.courseId, e] as const),
      ),
    [myEnrollments],
  )

  // ---- catalog ----
  const [catalog, setCatalog] = useState<Page<Course> | null>(null)
  const [catalogLoading, setCatalogLoading] = useState(false)
  const [catalogError, setCatalogError] = useState('')
  const [catQuery, setCatQuery] = useState('')
  const [catQ, setCatQ] = useState('')
  const [catCategory, setCatCategory] = useState('')
  const [catPage, setCatPage] = useState(1)
  const [catSize, setCatSize] = useState(9)
  const [enrollingId, setEnrollingId] = useState<string | null>(null)

  useEffect(() => {
    const t = setTimeout(() => { setCatQ(catQuery.trim()); setCatPage(1) }, 300)
    return () => clearTimeout(t)
  }, [catQuery])

  const fetchCatalog = useCallback(async () => {
    setCatalogLoading(true)
    try {
      const params = new URLSearchParams({ page: String(catPage), size: String(catSize), sort: 'createdAt', order: 'DESC' })
      if (catQ) params.set('q', catQ)
      if (catCategory) params.set('category', catCategory)
      setCatalog(await apiJson<Page<Course>>(`/courses?${params}`))
      setCatalogError('')
    } catch (err) {
      setCatalogError(err instanceof Error ? err.message : 'Failed to load catalog')
    } finally {
      setCatalogLoading(false)
    }
  }, [catPage, catSize, catQ, catCategory])

  useEffect(() => {
    if (tab === 'catalog') fetchCatalog()
  }, [tab, fetchCatalog])

  const enroll = async (courseId: string) => {
    setEnrollingId(courseId)
    setCatalogError('')
    try {
      await apiJson(`/courses/${courseId}/enroll`, { method: 'POST' })
      await Promise.all([fetchMine(), fetchCatalog()])
    } catch (err) {
      setCatalogError(err instanceof Error ? err.message : 'Enroll failed')
    } finally {
      setEnrollingId(null)
    }
  }

  // ---- certifications ----
  const [certs, setCerts] = useState<Certification[] | null>(null)
  const [certsLoading, setCertsLoading] = useState(false)
  const [certsError, setCertsError] = useState('')

  const fetchCerts = useCallback(async () => {
    setCertsLoading(true)
    try {
      setCerts(await apiJson<Certification[]>('/certifications/me'))
      setCertsError('')
    } catch (err) {
      setCertsError(err instanceof Error ? err.message : 'Failed to load certifications')
    } finally {
      setCertsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (tab === 'certs') fetchCerts()
  }, [tab, fetchCerts])

  // ---- manage (admin/hr) ----
  const [manage, setManage] = useState<Page<Course> | null>(null)
  const [manageLoading, setManageLoading] = useState(false)
  const [manageError, setManageError] = useState('')
  const [manageStatus, setManageStatus] = useState('')
  const [managePage, setManagePage] = useState(1)
  const [manageSize, setManageSize] = useState(10)
  const [actingId, setActingId] = useState<string | null>(null)

  const fetchManage = useCallback(async () => {
    setManageLoading(true)
    try {
      const params = new URLSearchParams({ page: String(managePage), size: String(manageSize), sort: 'createdAt', order: 'DESC' })
      if (manageStatus) params.set('status', manageStatus)
      setManage(await apiJson<Page<Course>>(`/courses?${params}`))
      setManageError('')
    } catch (err) {
      setManageError(err instanceof Error ? err.message : 'Failed to load courses')
    } finally {
      setManageLoading(false)
    }
  }, [managePage, manageSize, manageStatus])

  useEffect(() => {
    if (tab === 'manage') fetchManage()
  }, [tab, fetchManage])

  const setStatus = async (c: Course, status: string) => {
    setActingId(c.id)
    setManageError('')
    try {
      await apiJson(`/courses/${c.id}`, { method: 'PATCH', body: JSON.stringify({ status }) })
      await fetchManage()
    } catch (err) {
      setManageError(err instanceof Error ? err.message : 'Update failed')
    } finally {
      setActingId(null)
    }
  }

  const removeCourse = async (c: Course) => {
    if (!window.confirm(`Delete “${c.title}” and all its enrollments?`)) return
    setActingId(c.id)
    setManageError('')
    try {
      await apiJson(`/courses/${c.id}`, { method: 'DELETE' })
      await fetchManage()
    } catch (err) {
      setManageError(err instanceof Error ? err.message : 'Delete failed')
    } finally {
      setActingId(null)
    }
  }

  const drop = async (e: Enrollment) => {
    setMineError('')
    try {
      await apiJson(`/enrollments/${e.id}/drop`, { method: 'POST' })
      await fetchMine()
    } catch (err) {
      setMineError(err instanceof Error ? err.message : 'Drop failed')
    }
  }

  const myActive = useMemo(
    () => (myEnrollments ?? []).filter((e) => e.status !== 'dropped'),
    [myEnrollments],
  )

  const tabs = [
    { id: 'mine' as const, label: 'My Learning', icon: GraduationCap },
    { id: 'catalog' as const, label: 'Catalog', icon: BookOpen },
    { id: 'certs' as const, label: 'Certifications', icon: Award },
    { id: 'calendar' as const, label: 'Calendar', icon: CalendarDays },
    ...(allowManage
      ? [
          { id: 'manage' as const, label: 'Manage', icon: Users },
          { id: 'compliance' as const, label: 'Compliance', icon: ClipboardCheck },
        ]
      : []),
  ]

  return (
    <div className="space-y-5">
      {/* Tabs + actions */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap gap-1 rounded-xl bg-slate-100 p-1 sm:w-fit">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 rounded-lg px-4 py-2 text-xs font-semibold transition-all ${tab === t.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
            >
              <t.icon size={13} /> {t.label}
            </button>
          ))}
        </div>
        {tab === 'manage' && allowManage && (
          <button
            onClick={() => navigate('/courses/new')}
            className="flex items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-4 py-2.5 text-sm font-semibold text-white shadow-lg shadow-brand-500/25 transition-all hover:shadow-xl hover:brightness-110"
          >
            <Plus size={16} /> New course
          </button>
        )}
      </div>

      {/* ---- My learning ---- */}
      {tab === 'mine' && (
        <div className="space-y-4">
          {mineError && (
            <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={16} className="shrink-0" /> {mineError}
            </div>
          )}
          {myEnrollments === null && !mineError ? (
            <div className="flex h-48 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
            </div>
          ) : myActive.length === 0 ? (
            <div className="flex flex-col items-center rounded-2xl border border-slate-200 bg-white py-14 text-center shadow-sm">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                <GraduationCap size={22} />
              </div>
              <p className="mt-3 text-sm font-semibold">No courses yet</p>
              <p className="mt-1 text-xs text-slate-400">Browse the catalog and enroll in your first course.</p>
              <button
                onClick={() => setTab('catalog')}
                className="mt-4 rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-4 py-2 text-xs font-semibold text-white shadow-lg shadow-brand-500/25 hover:brightness-110"
              >
                Browse catalog
              </button>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {myActive.map((e) => {
                const cat = categoryMeta[e.course?.category ?? 'technical'] ?? categoryMeta.technical
                const st = enrollStatusMeta[e.status]
                const req = assignmentByCourse.get(e.courseId)
                const reqActive = req && req.status === 'assigned'
                return (
                  <div key={e.id} className={`flex flex-col rounded-2xl border bg-white p-5 shadow-sm ${reqActive && req.compliance === 'overdue' ? 'border-red-200 ring-1 ring-red-100' : 'border-slate-200'}`}>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${cat.cls}`}>{cat.label}</span>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${st.cls}`}>{st.label}</span>
                      {reqActive && (
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${complianceMeta[req.compliance].cls}`}>
                          <ClipboardCheck size={10} />
                          {req.mandatory ? 'Required' : 'Assigned'} · due {req.dueDate}
                        </span>
                      )}
                    </div>
                    <h3 className="mt-2 text-sm font-bold tracking-tight">{e.courseName}</h3>
                    <p className="mt-1 flex items-center gap-2 text-xs text-slate-400">
                      {e.course && <span>{formatLabel(e.course.format)}</span>}
                      {e.course?.durationHours ? <span>· {e.course.durationHours}h</span> : null}
                      <span>· enrolled {fmtDate(e.enrolledAt)}</span>
                    </p>
                    <div className="mt-4">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium text-slate-500">Progress</span>
                        <span className="font-bold text-slate-700">{e.progress}%</span>
                      </div>
                      <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-slate-100">
                        <div
                          className={`h-full rounded-full transition-all ${e.status === 'completed' ? 'bg-emerald-500' : 'bg-gradient-to-r from-brand-500 to-brand-600'}`}
                          style={{ width: `${e.progress}%` }}
                        />
                      </div>
                    </div>
                    <div className="mt-4 flex items-center gap-2">
                      <button
                        onClick={() => navigate(`/courses/${e.courseId}`)}
                        className="flex-1 rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-3 py-2 text-xs font-semibold text-white shadow-md shadow-brand-500/20 hover:brightness-110"
                      >
                        {e.status === 'completed' ? 'Review' : e.progress > 0 ? 'Continue' : 'Start'}
                      </button>
                      {e.status !== 'completed' && (
                        <button
                          onClick={() => drop(e)}
                          className="rounded-xl border border-slate-200 px-3 py-2 text-xs font-semibold text-slate-500 hover:bg-slate-50"
                        >
                          Drop
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ---- Catalog ---- */}
      {tab === 'catalog' && (
        <div className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <div className="relative">
              <Search size={15} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                value={catQuery}
                onChange={(e) => setCatQuery(e.target.value)}
                placeholder="Search courses…"
                className="w-full rounded-xl border border-slate-200 bg-white py-2.5 pl-10 pr-4 text-sm outline-none transition-all placeholder:text-slate-400 focus:border-brand-400 focus:ring-4 focus:ring-brand-100 sm:w-64"
              />
            </div>
            <select
              value={catCategory}
              onChange={(e) => { setCatCategory(e.target.value); setCatPage(1) }}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-600 outline-none transition-all focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
            >
              <option value="">All categories</option>
              {Object.entries(categoryMeta).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>

          {catalogError && (
            <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={16} className="shrink-0" /> {catalogError}
            </div>
          )}

          {catalogLoading && !catalog ? (
            <div className="flex h-48 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
            </div>
          ) : (catalog?.data ?? []).length === 0 ? (
            <div className="flex flex-col items-center rounded-2xl border border-slate-200 bg-white py-14 text-center shadow-sm">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                <BookOpen size={22} />
              </div>
              <p className="mt-3 text-sm font-semibold">No courses found</p>
              <p className="mt-1 text-xs text-slate-400">Try a different search or category.</p>
            </div>
          ) : (
            <>
              <div className={`grid gap-4 transition-opacity sm:grid-cols-2 xl:grid-cols-3 ${catalogLoading ? 'opacity-60' : ''}`}>
                {(catalog?.data ?? []).map((c) => {
                  const cat = categoryMeta[c.category] ?? categoryMeta.technical
                  const mine = enrolledCourseIds.get(c.id)
                  const req = assignmentByCourse.get(c.id)
                  const reqActive = req && req.status === 'assigned'
                  const full = c.capacity > 0 && c.enrolledCount >= c.capacity
                  return (
                    <div key={c.id} className="flex flex-col rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${cat.cls}`}>{cat.label}</span>
                        {c.certValidityMonths && (
                          <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 ring-1 ring-amber-100">
                            <Award size={10} /> Certificate
                          </span>
                        )}
                        {reqActive && (
                          <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${complianceMeta[req.compliance].cls}`}>
                            <ClipboardCheck size={10} />
                            {req.mandatory ? 'Required' : 'Assigned'} · due {req.dueDate}
                          </span>
                        )}
                      </div>
                      <button onClick={() => navigate(`/courses/${c.id}`)} className="mt-2 text-left text-sm font-bold tracking-tight hover:text-brand-700">
                        {c.title}
                      </button>
                      <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-500">{c.description ?? '—'}</p>
                      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-slate-400">
                        <span>{formatLabel(c.format)}</span>
                        {c.durationHours ? (
                          <span className="inline-flex items-center gap-1"><Clock size={11} /> {c.durationHours}h</span>
                        ) : null}
                        <span className="inline-flex items-center gap-1">
                          <Users size={11} /> {c.enrolledCount}{c.capacity > 0 ? `/${c.capacity}` : ''} enrolled
                        </span>
                        {c.instructorName && <span>· {c.instructorName}</span>}
                      </div>
                      <div className="mt-4">
                        {mine ? (
                          <button
                            onClick={() => navigate(`/courses/${c.id}`)}
                            className="w-full rounded-xl border border-brand-200 bg-brand-50 px-3 py-2 text-xs font-semibold text-brand-700 hover:bg-brand-100"
                          >
                            {mine.status === 'completed' ? (
                              <span className="inline-flex items-center gap-1"><CheckCircle2 size={12} /> Completed · Review</span>
                            ) : (
                              `Enrolled · ${mine.progress}%`
                            )}
                          </button>
                        ) : (
                          <button
                            onClick={() => enroll(c.id)}
                            disabled={enrollingId === c.id || full}
                            className="w-full rounded-xl bg-gradient-to-r from-brand-600 to-brand-700 px-3 py-2 text-xs font-semibold text-white shadow-md shadow-brand-500/20 transition-all hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
                          >
                            {enrollingId === c.id ? 'Enrolling…' : full ? 'Course full' : 'Enroll'}
                          </button>
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                <Pager
                  page={catPage}
                  totalPages={catalog?.totalPages ?? 1}
                  total={catalog?.total ?? 0}
                  size={catSize}
                  onPage={setCatPage}
                  onSize={(s) => { setCatSize(s); setCatPage(1) }}
                />
              </div>
            </>
          )}
        </div>
      )}

      {/* ---- Certifications ---- */}
      {tab === 'certs' && (
        <div className="space-y-4">
          {certsError && (
            <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={16} className="shrink-0" /> {certsError}
            </div>
          )}
          {certsLoading && !certs ? (
            <div className="flex h-48 items-center justify-center">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600" />
            </div>
          ) : (certs ?? []).length === 0 ? (
            <div className="flex flex-col items-center rounded-2xl border border-slate-200 bg-white py-14 text-center shadow-sm">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                <Award size={22} />
              </div>
              <p className="mt-3 text-sm font-semibold">No certifications yet</p>
              <p className="mt-1 text-xs text-slate-400">Complete a course that grants a certificate to earn one.</p>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
              {(certs ?? []).map((c) => {
                const st = certStatusMeta[c.status]
                return (
                  <div key={c.id} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-amber-50 text-amber-600 ring-1 ring-amber-100">
                        <Award size={18} />
                      </div>
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${st.cls}`}>{st.label}</span>
                    </div>
                    <h3 className="mt-3 text-sm font-bold tracking-tight">{c.courseName}</h3>
                    <p className="mt-1 text-xs text-slate-400">
                      Issued {fmtDate(c.issuedAt)} · {c.expiresAt ? `expires ${fmtDate(c.expiresAt)}` : 'no expiry'}
                    </p>
                    {c.status === 'expired' && (
                      <button
                        onClick={() => navigate(`/courses/${c.courseId}`)}
                        className="mt-3 w-full rounded-xl border border-brand-200 bg-brand-50 px-3 py-2 text-xs font-semibold text-brand-700 hover:bg-brand-100"
                      >
                        Re-take course to renew
                      </button>
                    )}
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* ---- Calendar ---- */}
      {tab === 'calendar' && <LearningCalendar />}

      {/* ---- Compliance (admin/hr) ---- */}
      {tab === 'compliance' && allowManage && <Compliance />}

      {/* ---- Manage (admin/hr) ---- */}
      {tab === 'manage' && allowManage && (
        <div className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
            <select
              value={manageStatus}
              onChange={(e) => { setManageStatus(e.target.value); setManagePage(1) }}
              className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-600 outline-none transition-all focus:border-brand-400 focus:ring-4 focus:ring-brand-100"
            >
              <option value="">All statuses</option>
              <option value="draft">Draft</option>
              <option value="pending">Pending</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {manageError && (
            <div className="flex items-center gap-2.5 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
              <AlertCircle size={16} className="shrink-0" /> {manageError}
            </div>
          )}

          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className={`transition-opacity ${manageLoading ? 'opacity-60' : ''}`}>
              <table className="w-full min-w-[720px]">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50/60">
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Course</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Category</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Status</th>
                    <th className="px-5 py-3.5 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-400">Enrolled</th>
                    <th className="px-5 py-3.5 text-right text-[11px] font-semibold uppercase tracking-wider text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(manage?.data ?? []).map((c) => {
                    const cat = categoryMeta[c.category] ?? categoryMeta.technical
                    const st = statusMeta[c.status]
                    return (
                      <tr key={c.id} className="transition-colors hover:bg-slate-50/70">
                        <td className="px-5 py-3.5">
                          <button onClick={() => navigate(`/courses/${c.id}`)} className="text-left text-sm font-semibold hover:text-brand-700">
                            {c.title}
                          </button>
                          <p className="text-xs text-slate-400">{formatLabel(c.format)}{c.durationHours ? ` · ${c.durationHours}h` : ''}{c.instructorName ? ` · ${c.instructorName}` : ''}</p>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${cat.cls}`}>{cat.label}</span>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className={`inline-flex rounded-full px-2 py-0.5 text-[11px] font-semibold ring-1 ${st.cls}`}>{st.label}</span>
                        </td>
                        <td className="px-5 py-3.5 text-sm text-slate-500">
                          {c.enrolledCount}{c.capacity > 0 ? `/${c.capacity}` : ''}
                        </td>
                        <td className="px-5 py-3.5 text-right">
                          <div className="inline-flex items-center gap-1">
                            {c.status === 'draft' || c.status === 'pending' ? (
                              <button
                                onClick={() => setStatus(c, 'published')}
                                disabled={actingId === c.id}
                                className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-emerald-600 transition-colors hover:bg-emerald-50 disabled:opacity-40"
                              >
                                Publish
                              </button>
                            ) : c.status === 'published' ? (
                              <button
                                onClick={() => setStatus(c, 'archived')}
                                disabled={actingId === c.id}
                                className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-slate-500 transition-colors hover:bg-slate-100 disabled:opacity-40"
                              >
                                Archive
                              </button>
                            ) : (
                              <button
                                onClick={() => setStatus(c, 'published')}
                                disabled={actingId === c.id}
                                className="rounded-lg px-2.5 py-1.5 text-xs font-semibold text-emerald-600 transition-colors hover:bg-emerald-50 disabled:opacity-40"
                              >
                                Republish
                              </button>
                            )}
                            <button
                              onClick={() => navigate(`/courses/${c.id}/edit`, { state: { course: c } })}
                              className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-brand-50 hover:text-brand-600"
                              title="Edit"
                            >
                              <Pencil size={15} />
                            </button>
                            {isAdmin && (
                              <button
                                onClick={() => removeCourse(c)}
                                disabled={actingId === c.id}
                                className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                                title="Delete"
                              >
                                <Trash2 size={15} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
            {(manage?.data ?? []).length === 0 && !manageLoading && !manageError && (
              <div className="flex flex-col items-center py-14 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
                  <BookOpen size={22} />
                </div>
                <p className="mt-3 text-sm font-semibold">No courses yet</p>
                <p className="mt-1 text-xs text-slate-400">Create the first course to build the catalog.</p>
              </div>
            )}
            <Pager
              page={managePage}
              totalPages={manage?.totalPages ?? 1}
              total={manage?.total ?? 0}
              size={manageSize}
              onPage={setManagePage}
              onSize={(s) => { setManageSize(s); setManagePage(1) }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
